
import React, { useState, useMemo } from 'react';
import { Bookmark, BookmarkCategory } from '../types';

interface BookmarkDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (bookmark: Omit<Bookmark, 'id' | 'timestamp'>) => void;
    onDelete: (id: string) => void;
    bookmarks: Bookmark[];
    categories: BookmarkCategory[];
    onAddCategory: (name: string, parentId: string | null) => void;
    onUpdateCategory: (id: string, newName: string) => void;
    onDeleteCategory: (id: string) => void;
    initialTitle: string;
    fileId: string;
    fileName: string;
    paragraphId?: string;
    paragraphText?: string;
    existingBookmark?: Bookmark;
}

const BookmarkDialog: React.FC<BookmarkDialogProps> = ({
    isOpen,
    onClose,
    onSave,
    onDelete,
    bookmarks,
    categories,
    onAddCategory,
    onUpdateCategory,
    onDeleteCategory,
    initialTitle,
    fileId,
    fileName,
    paragraphId,
    paragraphText,
    existingBookmark
}) => {
    const [title, setTitle] = useState(initialTitle);
    const [selectedCategoryPath, setSelectedCategoryPath] = useState('General');
    const [newCategoryName, setNewCategoryName] = useState('');
    const [isAddingCategory, setIsAddingCategory] = useState(false);
    const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
    const [editingCategoryName, setEditingCategoryName] = useState('');
    const [deletingCategoryId, setDeletingCategoryId] = useState<string | null>(null);
    const [note, setNote] = useState('');
    const [activeTab, setActiveTab] = useState<'save' | 'list'>('save');

    // Reset state when opening
    React.useEffect(() => {
        if (isOpen) {
            setTitle(initialTitle);
            setNote(existingBookmark?.note || '');
            setSelectedCategoryPath(existingBookmark?.categoryPath || 'General');
            setActiveTab('save');
        }
    }, [isOpen, initialTitle, existingBookmark]);

    const sortedCategories = useMemo(() => {
        const cats = [...categories];
        if (!cats.find(c => c.path === 'General')) {
            cats.unshift({ id: 'general', name: 'General', parentId: null, path: 'General' });
        }
        return cats.sort((a, b) => a.path.localeCompare(b.path));
    }, [categories]);

    if (!isOpen) return null;

    const handleSave = () => {
        onSave({
            title: title.trim() || fileName,
            fileId,
            fileName,
            paragraphId,
            paragraphText,
            categoryPath: selectedCategoryPath,
            note
        });
        onClose();
    };

    const handleAddCategory = () => {
        if (newCategoryName.trim()) {
            onAddCategory(newCategoryName.trim(), null);
            setNewCategoryName('');
            setIsAddingCategory(false);
        }
    };

    const handleUpdateCategory = () => {
        if (editingCategoryId && editingCategoryName.trim()) {
            onUpdateCategory(editingCategoryId, editingCategoryName.trim());
            setEditingCategoryId(null);
            setEditingCategoryName('');
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center pt-0 px-4 pb-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-stone-900 w-full max-w-md rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-stone-100 dark:border-stone-800 flex items-center justify-between bg-stone-50/50 dark:bg-stone-800/20">
                    <div className="flex gap-1 bg-stone-100 dark:bg-stone-800 p-1 rounded-xl">
                        <button 
                            onClick={() => setActiveTab('save')}
                            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'save' ? 'bg-white dark:bg-stone-700 text-amber-600 shadow-sm' : 'text-stone-500 hover:text-stone-800 dark:hover:text-stone-200'}`}
                        >
                            Save
                        </button>
                        <button 
                            onClick={() => setActiveTab('list')}
                            className={`px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === 'list' ? 'bg-white dark:bg-stone-700 text-amber-600 shadow-sm' : 'text-stone-500 hover:text-stone-800 dark:hover:text-stone-200'}`}
                        >
                            History ({bookmarks.length})
                        </button>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 hover:bg-stone-100 dark:hover:bg-stone-800 transition-all">
                        <i className="fa-solid fa-xmark"></i>
                    </button>
                </div>

                <div className="overflow-y-auto flex-1">
                    {activeTab === 'save' ? (
                        <div className="p-6 space-y-5">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center text-amber-500">
                                    <i className="fa-solid fa-bookmark"></i>
                                </div>
                                <div>
                                    <h3 className="text-lg font-serif font-bold text-stone-800 dark:text-stone-100">
                                        {existingBookmark ? 'Update Bookmark' : 'Add to Collection'}
                                    </h3>
                                    <p className="text-xs text-stone-500">{fileName}</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1.5 ml-1">Label / Title</label>
                                    <input 
                                        type="text" 
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        className="w-full px-4 py-3 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-stone-800 dark:text-stone-100 transition-all text-sm font-medium"
                                        placeholder="Give your bookmark a name..."
                                    />
                                </div>

                                {paragraphText && (
                                    <div className="bg-amber-50/50 dark:bg-amber-900/10 p-4 rounded-xl border border-amber-100/50 dark:border-amber-900/20 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-2 opacity-10">
                                            <i className="fa-solid fa-quote-right text-4xl"></i>
                                        </div>
                                        <label className="block text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase mb-2">Reference passage</label>
                                        <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed italic line-clamp-4">"{paragraphText}"</p>
                                    </div>
                                )}

                                <div>
                                    <div className="flex items-center justify-between mb-1.5 ml-1">
                                        <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest">Collection Folder</label>
                                        <button 
                                            onClick={() => setIsAddingCategory(!isAddingCategory)}
                                            className="text-[10px] font-bold text-amber-600 hover:text-amber-700 uppercase tracking-wider"
                                        >
                                            {isAddingCategory ? 'Back to Selection' : '+ New Folder'}
                                        </button>
                                    </div>

                                    {isAddingCategory ? (
                                        <div className="flex gap-2 animate-in slide-in-from-top-2 duration-200">
                                            <input 
                                                type="text" 
                                                value={newCategoryName}
                                                onChange={(e) => setNewCategoryName(e.target.value)}
                                                className="flex-1 px-4 py-2.5 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-sm"
                                                placeholder="Folder name..."
                                                autoFocus
                                            />
                                            <button 
                                                onClick={handleAddCategory}
                                                disabled={!newCategoryName.trim()}
                                                className="px-5 bg-amber-500 text-white rounded-xl font-bold text-sm hover:bg-amber-600 transition-colors disabled:opacity-50"
                                            >
                                                Add
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <select 
                                                value={selectedCategoryPath}
                                                onChange={(e) => setSelectedCategoryPath(e.target.value)}
                                                className="w-full px-4 py-3 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-stone-800 dark:text-stone-100 text-sm appearance-none cursor-pointer font-medium"
                                            >
                                                {sortedCategories.map(cat => (
                                                    <option key={cat.id} value={cat.path}>{cat.path}</option>
                                                ))}
                                            </select>
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-stone-400">
                                                <i className="fa-solid fa-chevron-down text-[10px]"></i>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1.5 ml-1">Personal Notes</label>
                                    <textarea 
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        className="w-full px-4 py-3 bg-stone-50 dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-xl outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 text-stone-800 dark:text-stone-100 text-sm min-h-[100px] resize-none font-medium leading-relaxed"
                                        placeholder="Reflect on this passage..."
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-0">
                            {/* Categories Section */}
                            <div className="p-4 bg-stone-50/50 dark:bg-stone-800/10 border-b border-stone-100 dark:border-stone-800">
                                <h4 className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-3">Managed Folders</h4>
                                <div className="flex flex-wrap gap-2">
                                    {categories.filter(c => c.id !== 'general').map(cat => (
                                        <div key={cat.id} className="relative">
                                            {deletingCategoryId === cat.id ? (
                                                <div className="flex items-center gap-1 px-3 py-1.5 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-lg text-[10px] animate-in slide-in-from-right-2 duration-200">
                                                    <span className="text-red-600 dark:text-red-400 font-bold">Delete?</span>
                                                    <button onClick={() => { onDeleteCategory(cat.id); setDeletingCategoryId(null); }} className="text-red-700 dark:text-red-300 font-black px-1 hover:scale-110 transition-transform">YES</button>
                                                    <button onClick={() => setDeletingCategoryId(null)} className="text-stone-400 px-1 font-bold">NO</button>
                                                </div>
                                            ) : editingCategoryId === cat.id ? (
                                                <div className="flex items-center gap-1 bg-white dark:bg-stone-800 border-2 border-amber-500 rounded-lg overflow-hidden animate-in slide-in-from-top-1 duration-200">
                                                    <input 
                                                        type="text" 
                                                        autoFocus
                                                        value={editingCategoryName} 
                                                        onChange={(e) => setEditingCategoryName(e.target.value)}
                                                        onKeyDown={(e) => { if (e.key === 'Enter') handleUpdateCategory(); if (e.key === 'Escape') setEditingCategoryId(null); }}
                                                        className="px-2 py-1 text-xs outline-none bg-transparent w-24"
                                                    />
                                                    <button onClick={handleUpdateCategory} className="bg-amber-500 text-white px-2 py-1 flex items-center justify-center">
                                                        <i className="fa-solid fa-check text-[10px]"></i>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="group flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-lg text-xs transition-all hover:border-amber-500/30">
                                                    <span className="text-stone-600 dark:text-stone-300 font-medium">{cat.name}</span>
                                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <button 
                                                            onClick={() => {
                                                                setEditingCategoryId(cat.id);
                                                                setEditingCategoryName(cat.name);
                                                            }}
                                                            className="text-stone-400 hover:text-amber-500 transition-colors"
                                                            title="Rename folder"
                                                        >
                                                            <i className="fa-solid fa-pen text-[10px]"></i>
                                                        </button>
                                                        <button 
                                                            onClick={() => setDeletingCategoryId(cat.id)}
                                                            className="text-stone-400 hover:text-red-500 transition-colors"
                                                            title="Delete folder"
                                                        >
                                                            <i className="fa-solid fa-circle-xmark text-[10px]"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {categories.length <= 1 && (
                                        <p className="text-stone-400 text-[10px] italic">No custom folders yet.</p>
                                    )}
                                </div>
                            </div>

                            {/* Bookmarks List */}
                            <div className="divide-y divide-stone-100 dark:divide-stone-800">
                                {bookmarks.length === 0 ? (
                                    <div className="p-10 text-center space-y-3">
                                        <div className="w-12 h-12 bg-stone-100 dark:bg-stone-800 rounded-full flex items-center justify-center mx-auto text-stone-300">
                                            <i className="fa-solid fa-bookmark text-xl"></i>
                                        </div>
                                        <p className="text-stone-500 text-xs italic">Your collection is empty.</p>
                                    </div>
                                ) : (
                                    bookmarks.map(b => (
                                        <div key={b.id} className="p-4 hover:bg-stone-50/50 dark:hover:bg-white/5 transition-colors group">
                                            <div className="flex justify-between items-start gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <span className="px-2 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-[9px] font-bold text-amber-700 dark:text-amber-400 rounded-md uppercase tracking-wider">
                                                            {b.categoryPath}
                                                        </span>
                                                        {b.paragraphId && (
                                                            <span className="px-2 py-0.5 bg-stone-100 dark:bg-stone-800 text-[9px] font-bold text-stone-500 rounded-md uppercase tracking-wider">
                                                                Passage
                                                            </span>
                                                        )}
                                                    </div>
                                                    <h5 className="text-sm font-serif font-bold text-stone-800 dark:text-stone-100 truncate">{(b.title && b.title.trim()) || b.fileName || 'Untitled Bookmark'}</h5>
                                                    <p className="text-[10px] text-stone-400 mt-0.5 line-clamp-1">{b.fileName}</p>
                                                    {b.note && <p className="text-[11px] text-stone-500 mt-2 line-clamp-2 italic italic-amber leading-relaxed">"{b.note}"</p>}
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    <button 
                                                        onClick={() => onDelete(b.id)}
                                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-stone-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all opacity-0 group-hover:opacity-100"
                                                        title="Delete bookmark"
                                                    >
                                                        <i className="fa-solid fa-trash-can text-xs"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {activeTab === 'save' && (
                    <div className="p-6 bg-stone-50 dark:bg-stone-800/50 border-t border-stone-100 dark:border-stone-800 flex gap-3">
                        {existingBookmark ? (
                            <button 
                                onClick={() => { onDelete(existingBookmark.id); onClose(); }}
                                className="flex-1 py-3 rounded-xl border border-red-200 text-red-600 font-bold text-sm hover:bg-red-50 transition-colors"
                            >
                                Remove
                            </button>
                        ) : (
                            <button 
                                onClick={onClose}
                                className="flex-1 py-3 rounded-xl border border-stone-200 dark:border-stone-700 text-stone-600 dark:text-stone-400 font-bold text-sm hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                            >
                                Cancel
                            </button>
                        )}
                        <button 
                            onClick={handleSave}
                            className="flex-1 py-3 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 transition-colors shadow-lg shadow-amber-500/20"
                        >
                            {existingBookmark ? 'Update' : 'Confirm Save'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookmarkDialog;
